import { isApolloError } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Row, Col, Divider, Form, Button, message, Alert, Space } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import { differenceInYears } from 'date-fns';
import {
  useGetDonorLazyQuery,
  useUpdateDonorMutation,
  DonorOutput,
  UserRole,
} from 'gql/types';
import { FormItem, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import CollectedMlsChart from 'modules/donor/components/CollectedMlsChart';
import DonorAvatar from 'modules/donor/components/DonorAvatar';
import DonorStatsPanel from 'modules/donor/components/DonorStatsPanel';
import AdditionalFormSection from 'modules/donor/components/EditDonorForm/AdditionalSection';
import { GET_DONOR_QUERY } from 'modules/donor/graphql/queries';
import { useDonorAvatarUrl } from 'modules/donor/hooks/useDonorAvatarUrl';
import { getCachedDonors } from 'modules/donor/utils/donor-cache';
import { shouldRetireDonor } from 'modules/donor/utils/get-allowed-donor-age';
import { isDonorAllowedToAutoRetire } from 'modules/donor/utils/get-status-rules';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { getDefaultParams } from './defaultParams';
import { DonorManagementButton } from './DonorManagementButton';
import DonorMicrochipSection from './DonorMicrochipSection';
import DonorStatusSection from './DonorStatusSection';
import OptionalFormSection from './OptionalSection';
import { prepareDataForDonorUpdating } from './prepareDataForUpdate';
import PetFormSection from './RequiredSection';
import SaveButton from './SaveButton';
import { editDonorSchema, EditDonorSchemaType } from './schema';
import styles from './styles.module.scss';

import ReadOnlyDatePicker from '../../../common/components/ReadOnlyDatePicker';

const EditDonorForm: FC = () => {
  const { t } = useTranslation('donor.EditDonorForm');

  const navigate = useNavigate();
  const pageParams = useParams();

  const [isEditMode, setIsEditMode] = useState(false);
  const isOffline = !navigator.onLine;

  const donorId = pageParams.id!;

  const editDonorForm = useForm<EditDonorSchemaType>({
    resolver: yupResolver(editDonorSchema),
    mode: 'onChange',
  });
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    getValues,
    reset,
    trigger,
  } = editDonorForm;

  const canUpdateDonors =
    useCurrentUser().data?.permissions?.updateDonorsData || false;
  const canViewVitalsChart =
    useCurrentUser().data?.permissions?.viewVitalsChart || false;
  const canChangeDonorStatus =
    useCurrentUser().data?.permissions?.changeDonorStatus || false;
  const canDeleteDonors = useCurrentUser().data?.role.value === UserRole.Admin;

  const [updateDonor, updateDonorMutation] = useUpdateDonorMutation({
    refetchQueries: [
      {
        query: GET_DONOR_QUERY,
        variables: { input: { id: donorId } },
      },
    ],
    onCompleted: (data) => {
      const donor = data.updateDonor as DonorOutput;
      const defaultParams = getDefaultParams(donor);
      reset(defaultParams);
      message.success(t('successMessage'));
    },
    onError: (error) => {
      const donor = getDonorQuery.data?.getDonor as DonorOutput;
      const defaultParams = getDefaultParams(donor);
      reset(defaultParams);
      reset(getValues());
      message.error(error.message);
    },
  });

  const [getDonor, getDonorQuery] = useGetDonorLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      const donor = data.getDonor as DonorOutput;
      const defaultParams = getDefaultParams(donor);
      reset(defaultParams);
    },
    onError: (err) => {
      if (isOffline) {
        return;
      }
      message.error(err.message);
      if (err.message === 'Not Found') {
        navigate(Routes.NotFound);
        return;
      }

      navigate(Routes.IndividualDonors);
    },
  });

  useEffect(() => {
    if (!donorId) {
      return;
    }

    if (isOffline) {
      const cached = getCachedDonors()[donorId];

      if (cached) {
        reset(getDefaultParams(cached as DonorOutput));
        message.info('Offline data from cache');
      } else {
        message.error('There are no saved data for this donor');
        navigate(Routes.IndividualDonors);
      }
      return;
    }

    getDonor({ variables: { input: { id: donorId } } }).catch((err) => {
      message.error(isApolloError(err) ? err.message : String(err));
      if (isApolloError(err) && err.message === 'Not Found') {
        navigate(Routes.NotFound);
      } else {
        navigate(Routes.IndividualDonors);
      }
    });
  }, [donorId]);

  const donor = getDonorQuery.data?.getDonor as DonorOutput;
  const isLoading = getDonorQuery.loading || updateDonorMutation.loading;
  const savedButtonDisabled = !(isValid && isDirty);

  const submitForm = (formData: EditDonorSchemaType) => {
    const preparedData = prepareDataForDonorUpdating(formData, donor);
    updateDonor({
      variables: {
        input: preparedData,
      },
    });
    setIsEditMode(false);
  };

  const editDonorHandler = handleSubmit(
    (formData) => {
      submitForm(formData);
    },
    () => {
      const formData = getValues();
      submitForm(formData);
    }
  );

  const handleSave = async () => {
    const formData = getValues();
    const preparedData = prepareDataForDonorUpdating(formData, donor);

    await updateDonor({
      variables: {
        input: preparedData,
      },
    });

    setIsEditMode(false);
  };

  const cancelHandler = () => {
    reset();
    setIsEditMode(false);
  };

  const turnOnEditMode = () => {
    trigger();
    setIsEditMode(true);
  };

  useEffect(() => {
    if (donorId) {
      getDonor({
        variables: {
          input: {
            id: donorId,
          },
        },
      });
    }
  }, [donorId]);

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  const avatarUrl = useDonorAvatarUrl(donor?.pictures);
  const specie = editDonorForm.watch('required.species');
  const weight = editDonorForm.watch('required.weight');

  useEffect(() => {
    if (weight) {
      editDonorForm.trigger('required.weight');
    }
  }, [specie, weight]);

  const dateOfBirth = editDonorForm.watch(`required.dateOfBirth`);

  useEffect(() => {
    const now = new Date();
    const newAge = differenceInYears(now, dateOfBirth);
    editDonorForm.setValue('required.age', newAge);
  }, [dateOfBirth]);

  const age = editDonorForm.watch('required.age');
  const status = editDonorForm.watch('donorStatus.status');

  const isAllowedToAutoRetire = isDonorAllowedToAutoRetire(status);
  const shouldRetire = shouldRetireDonor(specie, age);
  const willRetire = isAllowedToAutoRetire && shouldRetire;

  return (
    <>
      <Spin spinning={isLoading}>
        <Space direction="vertical" size={24} className={styles.formWrapper}>
          <Row justify="space-between">
            <Col>
              {canDeleteDonors && donor && (
                <DonorManagementButton
                  donorId={donor?.id}
                  donorType={donor?.type}
                  donorStatus={donor?.status}
                />
              )}
            </Col>
            <Col>
              {canUpdateDonors && (
                <Row justify="end">
                  <Col className={styles.cancelButtonWrapper}>
                    <Button onClick={cancelHandler} disabled={!isEditMode}>
                      {t('cancel')}
                    </Button>
                  </Col>
                  <Col>
                    {isEditMode ? (
                      <SaveButton
                        disabled={savedButtonDisabled}
                        formIsValid={isValid}
                        onSave={editDonorHandler}
                      />
                    ) : (
                      <Button type="primary" onClick={turnOnEditMode}>
                        {t('edit')}
                      </Button>
                    )}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
          {willRetire && isEditMode && (
            <Row>
              <Col span={24}>
                <Alert
                  type="warning"
                  message={t('donorAutoRetirementAlertMessage')}
                />
              </Col>
            </Row>
          )}
          <FormProvider {...editDonorForm}>
            <Form labelCol={{ span: 24 }}>
              <Row>
                <Col span={24}>
                  <Row justify="space-between" gutter={[10, 10]}>
                    <Col sm={{ order: 1, span: 12 }} span={24} order={2}>
                      <PetFormSection
                        fieldNamePrefix="required"
                        disabled={!isEditMode}
                        ownerId={donor?.user?.id}
                        donor={donor}
                      />
                    </Col>
                    <Col lg={6} sm={{ order: 2, span: 12 }} span={24} order={1}>
                      {getDonorQuery?.data?.getDonor.createdAt && (
                        <FormItem label={t('dateOfCreation')}>
                          <ReadOnlyDatePicker
                            value={
                              new Date(getDonorQuery.data.getDonor.createdAt)
                            }
                            format={config.DATE_FORMATS.DATE}
                          />
                        </FormItem>
                      )}
                      <DonorStatusSection
                        fieldNamePrefix="donorStatus"
                        disabled={!isEditMode}
                        shouldRetire={shouldRetire}
                        canChangeDonorStatus={canChangeDonorStatus}
                      />
                      <DonorMicrochipSection
                        fieldNamePrefix="donorMicrochip"
                        disabled={!isEditMode}
                      />
                      {avatarUrl && (
                        <Col span={24}>
                          <DonorAvatar src={avatarUrl} />
                        </Col>
                      )}
                    </Col>
                  </Row>
                </Col>
                <Divider />
                <Col span={24}>
                  <Row gutter={[32, 0]}>
                    <Col sm={12} span={24}>
                      <OptionalFormSection
                        fieldNamePrefix="optional"
                        disabled={!isEditMode}
                        donor={
                          getDonorQuery?.data?.getDonor as
                            | DonorOutput
                            | undefined
                        }
                      />
                    </Col>
                    <Col sm={12} span={24}>
                      {canViewVitalsChart && <CollectedMlsChart />}
                      <AdditionalFormSection
                        fieldNamePrefix="additional"
                        disabled={!isEditMode}
                      />
                      <DonorStatsPanel />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </FormProvider>
        </Space>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={updateDonorMutation.loading}
      />
    </>
  );
};

export default EditDonorForm;
